import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip,
    Grid
} from "@mui/material";
import {
    CheckCircle,
    Error,
    Warning,
    Info,
    Refresh,
    Login,
    Logout
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";

const AuthDebugger = () => {
    const { 
        currentUser, 
        userProfile, 
        loading, 
        role, 
        isAdmin,
        refreshUserData,
        login,
        logout 
    } = useAuth();
    
    const [debugSteps, setDebugSteps] = useState([]);
    const [testEmail, setTestEmail] = useState("admin@capivio.com");
    const [testPassword, setTestPassword] = useState("");

    const runDebugChecks = useCallback(() => {
        const steps = [];

        // Paso 1: Verificar si hay usuario de Firebase
        steps.push({
            id: "firebase_user",
            title: "Usuario de Firebase",
            status: currentUser ? "success" : "error",
            message: currentUser ? 
                `Usuario autenticado: ${currentUser.email}` : 
                "No hay usuario de Firebase autenticado",
            details: currentUser ? {
                uid: currentUser.uid,
                email: currentUser.email,
                emailVerified: currentUser.emailVerified
            } : null
        });

        // Paso 2: Verificar si hay perfil de usuario
        steps.push({
            id: "user_profile",
            title: "Perfil de Usuario",
            status: userProfile ? "success" : "error",
            message: userProfile ? 
                `Perfil cargado con rol: ${userProfile.role}` : 
                "No se pudo cargar el perfil del usuario",
            details: userProfile ? {
                role: userProfile.role,
                tier: userProfile.tier,
                balance: userProfile.balance,
                kycStatus: userProfile.kycStatus
            } : null
        });

        // Paso 3: Verificar estado de carga
        steps.push({
            id: "loading_state",
            title: "Estado de Carga",
            status: !loading ? "success" : "warning",
            message: loading ? 
                "Sistema aún cargando datos" : 
                "Sistema completamente cargado",
            details: { loading }
        });

        // Paso 4: Verificar rol del contexto
        steps.push({
            id: "context_role",
            title: "Rol del Contexto",
            status: role ? "success" : "error",
            message: role ? 
                `Rol detectado: ${role}` : 
                "No se detectó rol en el contexto",
            details: { role, isAdmin }
        });

        // Paso 5: Verificar permisos de administración
        if (userProfile) {
            const hasBusinessAccess = userProfile.role?.toUpperCase() === "SUPER_ADMIN" ||
                                    userProfile.role?.toUpperCase() === "BUSINESS_ADMIN" ||
                                    userProfile.role?.toUpperCase() === "ADMIN";
            
            const hasSystemAccess = userProfile.role?.toUpperCase() === "SUPER_ADMIN" ||
                                  userProfile.role?.toUpperCase() === "SYSTEM_ADMIN";

            steps.push({
                id: "admin_permissions",
                title: "Permisos de Administración",
                status: (hasBusinessAccess || hasSystemAccess) ? "success" : "error",
                message: (hasBusinessAccess || hasSystemAccess) ? 
                    "Usuario tiene permisos de administración" : 
                    "Usuario no tiene permisos de administración",
                details: {
                    hasBusinessAccess,
                    hasSystemAccess,
                    role: userProfile.role,
                    normalizedRole: userProfile.role?.toUpperCase()
                }
            });
        }

        // Paso 6: Verificar consistencia de datos
        if (currentUser && userProfile) {
            const roleConsistency = role === userProfile.role;
            steps.push({
                id: "data_consistency",
                title: "Consistencia de Datos",
                status: roleConsistency ? "success" : "warning",
                message: roleConsistency ? 
                    "Datos consistentes entre contexto y perfil" : 
                    "Inconsistencia entre rol del contexto y perfil",
                details: {
                    contextRole: role,
                    profileRole: userProfile.role,
                    isConsistent: roleConsistency
                }
            });
        }

        setDebugSteps(steps);
    }, [currentUser, userProfile, loading, role, isAdmin]);

    useEffect(() => {
        runDebugChecks();
    }, [runDebugChecks]);

    const getStatusIcon = (status) => {
        switch (status) {
        case "success":
            return <CheckCircle color="success" />;
        case "error":
            return <Error color="error" />;
        case "warning":
            return <Warning color="warning" />;
        default:
            return <Info color="info" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "success":
            return "success";
        case "error":
            return "error";
        case "warning":
            return "warning";
        default:
            return "info";
        }
    };

    const handleRefresh = async () => {
        try {
            await refreshUserData();
            runDebugChecks();
        } catch (error) {
            // Error refreshing user data
        }
    };

    const handleTestLogin = async () => {
        if (!testEmail || !testPassword) {
            alert("Por favor ingresa email y contraseña");
            return;
        }

        try {
            await login(testEmail, testPassword);
        } catch (error) {
            alert(`Error en login: ${error.message}`);
        }
    };

    const handleTestLogout = async () => {
        try {
            await logout();
        } catch (error) {
            // Error in logout
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                        Debugger de Autenticación
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        sx={{ mr: 1 }}
                    >
                        Refrescar
                    </Button>
                </Box>

                {/* Estado General */}
                <Alert 
                    severity={currentUser && userProfile ? "success" : "warning"} 
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h6">
                        Estado General: {currentUser && userProfile ? "AUTENTICADO" : "NO AUTENTICADO"}
                    </Typography>
                    {currentUser && userProfile && (
                        <Typography variant="body2">
                            Usuario: {currentUser.email} | Rol: {userProfile.role}
                        </Typography>
                    )}
                </Alert>

                {/* Pasos de Debug */}
                <Typography variant="h6" gutterBottom>
                    Verificaciones del Sistema ({debugSteps.length})
                </Typography>
                
                <List>
                    {debugSteps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <ListItem>
                                <ListItemIcon>
                                    {getStatusIcon(step.status)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography variant="subtitle1">
                                                {step.title}
                                            </Typography>
                                            <Chip 
                                                label={step.status.toUpperCase()} 
                                                color={getStatusColor(step.status)}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {step.message}
                                            </Typography>
                                            {step.details && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Detalles: {JSON.stringify(step.details, null, 2)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                            {index < debugSteps.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>

                {/* Acciones de Prueba */}
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="h6" gutterBottom>
                        Acciones de Prueba
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <input
                                type="email"
                                placeholder="Email de prueba"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <input
                                type="password"
                                placeholder="Contraseña"
                                value={testPassword}
                                onChange={(e) => setTestPassword(e.target.value)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                startIcon={<Login />}
                                onClick={handleTestLogin}
                                fullWidth
                            >
                                Test Login
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                startIcon={<Logout />}
                                onClick={handleTestLogout}
                                fullWidth
                            >
                                Test Logout
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    // Debug info logged to console
                                }}
                                fullWidth
                            >
                                Log a Consola
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {/* Información Adicional */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Información Adicional
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • Este debugger verifica cada paso del flujo de autenticación
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • Los iconos indican el estado de cada verificación
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • Usa &quot;Refrescar&quot; para actualizar los datos del usuario
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • &quot;Log a Consola&quot; muestra información detallada en la consola del navegador
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export default AuthDebugger; 